import { useSearchParams } from "react-router-dom";
import { Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { EnquiryForm } from "../components/EnquiryForm";
import { EMAIL, OFFICE, PHONE_DISPLAY, PHONE_LINK, WHATSAPP_LINK, projects } from "../data/site";

const channels = [
  {
    icon: Phone,
    label: "Call us",
    value: PHONE_DISPLAY,
    href: PHONE_LINK,
    testid: "contact-phone-link",
  },
  {
    icon: MessageCircle,
    label: "WhatsApp",
    value: "+91 77383 84698 — chat with our team",
    href: WHATSAPP_LINK,
    testid: "contact-whatsapp-link",
  },
  {
    icon: Mail,
    label: "Email",
    value: EMAIL,
    href: `mailto:${EMAIL}`,
    testid: "contact-email-link",
  },
  {
    icon: MapPin,
    label: "Office",
    value: OFFICE,
    testid: "contact-address",
  },
];

export default function Contact() {
  const [params] = useSearchParams();
  const slug = params.get("project");
  const preselected = projects.find((p) => p.slug === slug)?.name || "General";

  return (
    <main data-testid="contact-page">
      <SEO
        title="Contact — Parsn Group"
        description="Enquire about Parsn Group's redevelopment projects in Kurla and Chembur. Call 022 4099 9999 or write to connect@parsnindia.com."
        path="/contact"
      />
      <section className="bg-white pt-32 sm:pt-44 pb-16 sm:pb-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="overline-label mb-4">Contact</p>
            <h1 className="font-serif text-4xl sm:text-6xl text-ink tracking-tight leading-[1.05] max-w-3xl">
              Tell us what you're <span className="italic text-navy">planning.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-gray-600 leading-relaxed">
              A home in one of our ongoing projects, a society redevelopment, or a partnership —
              every enquiry reaches the Parsn team directly.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-paper border-t border-gray-200 py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 flex flex-col gap-px bg-gray-200 border border-gray-200">
            {channels.map((c, i) => {
              const Inner = (
                <>
                  <span className="w-11 h-11 shrink-0 bg-navy/5 text-navy flex items-center justify-center">
                    <c.icon size={18} />
                  </span>
                  <span>
                    <span className="block text-[11px] uppercase tracking-[0.2em] text-gray-400">{c.label}</span>
                    <span className="block mt-1 text-sm font-medium text-ink">{c.value}</span>
                  </span>
                </>
              );
              return (
                <Reveal key={c.label} delay={i * 0.06} className="bg-white">
                  {c.href ? (
                    <a
                      href={c.href}
                      data-testid={c.testid}
                      target={c.href.startsWith("http") ? "_blank" : undefined}
                      rel={c.href.startsWith("http") ? "noopener noreferrer" : undefined}
                      className="flex items-center gap-5 p-6 sm:p-8 transition-colors duration-300 hover:bg-paper"
                    >
                      {Inner}
                    </a>
                  ) : (
                    <div data-testid={c.testid} className="flex items-center gap-5 p-6 sm:p-8">{Inner}</div>
                  )}
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.1} className="lg:col-span-7 bg-white border border-gray-200 p-7 sm:p-12">
            <h2 className="font-serif text-2xl sm:text-3xl text-ink tracking-tight mb-8">Send an enquiry</h2>
            <EnquiryForm key={preselected} defaultProject={preselected} />
          </Reveal>
        </div>
      </section>
    </main>
  );
}
